import { Settings } from '@/settings'
const { INDEX_DB_NAME, INDEX_DB_VERSION } = Settings

let DB
let promise

/**
 * Gets the IndexedDB database singleton.
 * 
 * @param {String} name 
 * @param {Number} version 
 * @returns {Promise<IDBDatabase>}
 */
const getDB = async (name=undefined, version=undefined) => {
	if (DB) {
		return DB
	}
	
	if (promise) {
		return await promise
	}

	let resolve, reject
	promise = new Promise((res, rej) => {
		resolve = res
		reject = rej
	})

	let request

	try {
		request = indexedDB.open(name || INDEX_DB_NAME, version || INDEX_DB_VERSION)
	} catch (e) {
		console.error(e.message)
	}

	request.onerror = async (e) => {
		if (name && version) {
			console.error('Error opening indexedDB:', e.target.errorCode)
			reject(e)
			return
		}

		const res = await indexedDB.databases()
		const db = res.find((db) => db.name === INDEX_DB_NAME)

		if (db) {
			resolve(await getDB(INDEX_DB_NAME, db.version))
		} else {
			reject(new Error('Database not found'))
		}
	}

	request.onsuccess = (e) => {
		DB = e.target.result
		resolve(DB)
	}

	request.onupgradeneeded = (e) => {
		const db = e.target.result
        
		if (e.oldVersion < 1) {
			db.createObjectStore('http-cache', { keyPath: 'uid' })
			db.createObjectStore('store-cache', { keyPath: 'getter' })
		}
		
		if (e.oldVersion < 2) {
			db.createObjectStore('log-cache', { keyPath: 'uid' })
		}
	}

	return await promise
}

/**
 * Creates a transaction for the specified store.
 * 
 * @param {String} store - The name of the store to create a transaction for.
 * @param {Function} oncomplete - Optional callback for transaction completion.
 * @param {Function} onerror - Optional callback for transaction error handling.
 * @returns {Promise<{transaction: IDBTransaction, promise: Promise, resolve: Function, reject: Function}>}
 */
const getTransaction = async (store, oncomplete=undefined, onerror=undefined) => {
	let resolve, reject
	const promise = new Promise((res, rej) => {
		resolve = res
		reject = rej
	})

	const db = await getDB()
	const transaction = db.transaction([store], 'readwrite')

	transaction.oncomplete = oncomplete ? oncomplete : (e) => {
		resolve(e.target.result)
	}

	transaction.onerror = onerror ? onerror : (e) => {
		console.error('Error with transaction:', transaction.error)
		reject(e)
	}

	return { transaction, promise, resolve, reject }
}

/** 
 * Inserts a single item into the specified store.
 * 
 * @param {String} store - The name of the store to insert into.
 * @param {Object} data - The data to insert.
 * @returns {Promise<Object>} - The inserted data.
 */
const insert = async (store, data) => {
	const { transaction, promise } = await getTransaction(store)
	transaction.objectStore(store).add(data)

	return await promise
}

/**
 * Inserts multiple items into the specified store.
 * 
 * @param {String} store - The name of the store to insert into.
 * @param {Array<Object>} data - The data to insert.
 * @returns {Promise<Array<Object>>} - The inserted data.
 */
const insertMany = async (store, data) => {
	const { transaction, promise } = await getTransaction(store)
	const request = transaction.objectStore(store)

	data.forEach((item) => {
		request.add(item)
	})

	return await promise
}

/**
 * Upserts a single item into the specified store.
 * 
 * @param {String} store - The name of the store to upsert into.
 * @param {Object} data - The data to upsert.
 * @returns {Promise<Object>} - The upserted data.
 */
const upsert = async (store, data) => {
	const { transaction, promise } = await getTransaction(store)
	transaction.objectStore(store).put(data)

	return await promise
}

/**
 * Upserts multiple items into the specified store.
 * 
 * @param {String} store - The name of the store to upsert into.
 * @param {Array<Object>} data - The data to upsert.
 * @returns {Promise<Array<Object>>} - The upserted data.
 */
const upsertMany = async (store, data) => {
	const { transaction, promise } = await getTransaction(store)
	const request = transaction.objectStore(store)

	data.forEach((item) => {
		request.put(item)
	})

	return await promise
}

/**
 * Retrieves a single item from the specified store by key.
 * 
 * @param {String} store - The name of the store to retrieve from.
 * @param {String} key - The key of the item to retrieve.
 * @returns {Promise<Object>} - The retrieved item.
 */
const get = async (store, key) => {
	const { transaction, promise, resolve } = await getTransaction(store)
	transaction.objectStore(store).get(key).onsuccess = (e) => {
		resolve(e.target.result)
	}

	return await promise
}

/**
 * Retrieves all items from the specified store.
 * 
 * @param {String} store - The name of the store to retrieve from.
 * @returns {Promise<Array<Object>>} - An array of all items in the store.
 */
const list = async (store, page=0, pageSize=0) => {
	const { transaction, promise, resolve } = await getTransaction(store, (e) => {})

	if (!pageSize) {
		transaction.objectStore(store).getAll().onsuccess = (e) => {
			resolve(e.target.result)
		}
	} else {
		const request = transaction.objectStore(store).openCursor()
		const results = []
		let advanced = false
        
		request.onsuccess = (e) => {
			const cursor = e.target.result
			if (!cursor) {
				resolve(results)
				return
			}

			if (!advanced && page && pageSize) {
				cursor.advance(page * pageSize)
				advanced = true
				return 
			}

			if (results.length >= pageSize) {
				resolve(results)
				return
			}
            
			results.push(cursor.value)
			cursor.continue()
		}
	}

	return await promise
}

/**
 * Retrieves all keys from the specified store.
 * 
 * @param {String} store - The name of the store to retrieve keys from.
 * @returns {Promise<Array>} - An array of all keys in the store.
 */
const keys = async (store) => {
	const { transaction, promise, resolve } = await getTransaction(store, (e) => {})
	transaction.objectStore(store).getAllKeys().onsuccess = (e) => {
		resolve(e.target.result)
	}

	return await promise
}

/**
 * Removes a single item from the specified store by key.
 * 
 * @param {String} store - The name of the store to remove from.
 * @param {String} key - The key of the item to remove.
 * @returns {Promise<void>}
 */
const remove = async (store, key) => {
	const { transaction, promise } = await getTransaction(store)
	transaction.objectStore(store).delete(key)

	return await promise
}

/**
 * Iterates over each item in the specified store and applies a callback function.
 * 
 * @param {String} store - The name of the store to iterate over.
 * @param {Function} cb - The callback function to apply to each item.
 * @param {Boolean} reverse - If true, iterates in reverse order.
 * @returns {Promise<void>}
 */
const each = async (store, cb, reverse=false) => {
	const { transaction, promise, resolve } = await getTransaction(store)

	transaction.objectStore(store)
		.openCursor(null, reverse ? 'prev' : 'next')
		.onsuccess = (e) => {
			const cursor = e.target.result

			if (cursor) {
				cb(cursor)
				cursor.continue()
			} else {
				resolve()
			}
		}

	return await promise
}

/**
 * Clears all items from the specified store.
 * 
 * @param {String} store - The name of the store to clear.
 * @returns {Promise<void>}
 */
const clear = async (store) => {
	const { transaction, promise, resolve } = await getTransaction(store)
	
	transaction.objectStore(store).clear().onsuccess = (e) => {
		resolve(e.target.result)
	}

	return await promise
}

/**
 * Clears all items from all stores in the database.
 * 
 * @returns {Promise<void>}
 */
const clearAll = async () => {
	const db = await getDB()
	const storeNames = db.objectStoreNames

	for (const store of storeNames) {
		await clear(store)
	}
}

export const idb = {
	getDB,
	getTransaction,
	insert,
	insertMany,
	upsert,
	upsertMany,
	get,
	list,
	keys,
	remove,
	each,
	clear,
	clearAll,
}